import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, LogOut, Package, DollarSign, Calendar, ShoppingCart, Store, Home, Settings, Mail, CreditCard, Save, User, HelpCircle, Phone, Pencil, Copy, Receipt, Settings2, Bug, Handshake, ArrowRight, Users, TrendingUp, Wallet, Ticket } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import TransferRequestForm from "../components/TransferRequestForm";
import StripeConnectOnboarding from "../components/StripeConnectOnboarding";
import { formatDateFR, formatPriceFR, translateStatus } from "../utils/formatters";
import CopyableText, { CopyableBox } from "../components/CopyableText";
import OnboardingTutorial, { useTutorial } from "../components/OnboardingTutorial";
import TicketListingsManager from "../components/TicketListingsManager";
import InvoicesList from "../components/InvoicesList";

const DashboardPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [listingPrefillData, setListingPrefillData] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [showStripeSetup, setShowStripeSetup] = useState(false);
  
  // Promo code state
  const [promoCodeInfo, setPromoCodeInfo] = useState(null);
  const [copiedPromoCode, setCopiedPromoCode] = useState(false);
  
  // Partner dashboard preview state
  const [partnerPreview, setPartnerPreview] = useState(null);
  
  // Tutorial hook
  const { showTutorial, completeTutorial, skipTutorial, resetTutorial } = useTutorial();
  
  // Payment info state
  const [paymentInfo, setPaymentInfo] = useState({
    interac_email: '',
    ticketmaster_email: ''
  });
  const [savingPaymentInfo, setSavingPaymentInfo] = useState(false);
  const [showPaymentSettings, setShowPaymentSettings] = useState(false);
  
  // Profile edit state
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileData, setProfileData] = useState({
    email: user?.email || '',
    phone: user?.phone || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Polling: track previous transaction statuses
  const prevStatusesRef = useRef({});
  const isFirstLoadRef = useRef(true);

  // Auto-open transfer form when arriving from a public listing link (/billet/SLUG)
  useEffect(() => {
    const openTransfer = searchParams.get("openTransfer");
    const listingSlug = searchParams.get("listing");
    if (openTransfer === "1" && listingSlug) {
      (async () => {
        try {
          const res = await axios.get(`${API}/ticket-listings/by-slug/${listingSlug}/buyer-prefill`);
          setListingPrefillData(res.data);
          setShowCreateDialog(true);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Impossible de charger l'annonce");
        } finally {
          // Clean URL once handled
          setSearchParams({}, { replace: true });
        }
      })();
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchTransactions();
    fetchPaymentInfo();
    fetchProfile();
    fetchPromoCode();
    if (user?.role?.includes('partner')) {
      fetchPartnerPreview();
    }
    
    // Polling: check for updates every 30 seconds
    const pollInterval = setInterval(() => {
      fetchTransactionsPolling();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const u = response.data;
      setProfileData({
        email: u.email || '',
        phone: u.phone || '',
        first_name: u.first_name || '',
        last_name: u.last_name || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, profileData);
      toast.success('Profil mis à jour!');
      setShowProfileEdit(false);
      if (refreshUserData) refreshUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/payment-info`);
      setPaymentInfo({
        interac_email: response.data.interac_email || '',
        ticketmaster_email: response.data.ticketmaster_email || ''
      });
    } catch (error) {
      console.error('Error fetching payment info:', error);
    }
  };

  const fetchPromoCode = async () => {
    try {
      const response = await axios.get(`${API}/auth/my-promo-code`);
      if (response.data.has_promo_code) {
        setPromoCodeInfo(response.data);
      }
    } catch (error) {
      console.error('Error fetching promo code:', error);
    }
  };

  const copyPromoCode = () => {
    if (promoCodeInfo?.promo_code) {
      navigator.clipboard.writeText(promoCodeInfo.promo_code);
      setCopiedPromoCode(true);
      toast.success('Code promo copié!');
      setTimeout(() => setCopiedPromoCode(false), 2000);
    }
  };

  const fetchPartnerPreview = async () => {
    try {
      const response = await axios.get(`${API}/partners/my-dashboard`);
      setPartnerPreview(response.data);
    } catch (error) {
      console.error('Error fetching partner preview:', error);
    }
  };

  const savePaymentInfo = async () => {
    setSavingPaymentInfo(true);
    try {
      await axios.put(`${API}/auth/payment-info`, paymentInfo);
      toast.success('Informations de paiement sauvegardées!');
      setShowPaymentSettings(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingPaymentInfo(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions`);
      const data = response.data;
      // Store initial statuses without notifying
      const statusMap = {};
      data.forEach(t => { statusMap[t.id] = t.status; });
      prevStatusesRef.current = statusMap;
      isFirstLoadRef.current = false;
      setTransactions(data);
    } catch (error) {
      toast.error(t('notifications.failedToLoad'));
    }
    setLoading(false);
  };

  const fetchTransactionsPolling = async () => {
    try {
      const response = await axios.get(`${API}/transactions`);
      const data = response.data;
      const prev = prevStatusesRef.current;
      
      // Detect status changes and new transactions
      data.forEach(tx => {
        const oldStatus = prev[tx.id];
        if (oldStatus && oldStatus !== tx.status) {
          const statusLabel = translateStatus(tx.status);
          toast.info(
            `${tx.event_name} — ${language === 'fr' ? 'Statut mis à jour' : 'Status updated'}: ${statusLabel}`,
            { duration: 6000 }
          );
        } else if (!oldStatus && !isFirstLoadRef.current) {
          toast.info(
            `${language === 'fr' ? 'Nouvelle demande' : 'New request'}: ${tx.event_name}`,
            { duration: 6000 }
          );
        }
      });
      
      // Update stored statuses
      const statusMap = {};
      data.forEach(t => { statusMap[t.id] = t.status; });
      prevStatusesRef.current = statusMap;
      setTransactions(data);
    } catch (error) {
      // Silent fail on polling
    }
  };

  const handleTransactionCreated = (transaction) => {
    setShowCreateDialog(false);
    navigate(`/transactions/${transaction.id}`);
  };

  const getUserRole = (transaction) => {
    if (transaction.buyer_email === user.email || transaction.buyer_client_number === user.client_number) return "buyer";
    if (transaction.seller_email === user.email || transaction.seller_client_number === user.client_number) return "seller";
    return "creator";
  };

  const filterTransactions = (type) => {
    if (type === "all") return transactions;
    if (type === "buying") return transactions.filter(t => t.buyer_email === user.email || t.buyer_client_number === user.client_number);
    if (type === "selling") return transactions.filter(t => t.seller_email === user.email || t.seller_client_number === user.client_number);
    return transactions;
  };

  const getStatusColor = (status) => {
    const colors = {
      created: "bg-brand-100 text-blue-800",
      tickets_uploaded: "bg-yellow-100 text-yellow-800",
      ocr_verified: "bg-green-100 text-green-800",
      admin_verified: "bg-green-100 text-green-800",
      completed: "bg-emerald-100 text-emerald-800",
      disputed: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  // Utiliser le traducteur de statuts
  const formatStatus = (status) => {
    return translateStatus(status);
  };

  const getRoleDisplay = (role) => {
    if (role === "buyer") return { icon: ShoppingCart, text: t('dashboard.buying'), color: "text-brand-600" };
    if (role === "seller") return { icon: Store, text: t('dashboard.selling'), color: "text-green-600" };
    return { icon: Package, text: t('common.create'), color: "text-gray-600" };
  };

  const filteredTransactions = filterTransactions(activeTab);
  const buyingCount = transactions.filter(t => t.buyer_email === user.email || t.buyer_client_number === user.client_number).length;
  const sellingCount = transactions.filter(t => t.seller_email === user.email || t.seller_client_number === user.client_number).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div 
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={() => navigate('/')}
            >
              <img src="/logo-dark-cropped.png" alt="Middleman" className="h-5 md:h-6 w-auto" />
            </div>
            
            {/* Right side actions - responsive */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Home button - icon only on mobile */}
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-2 md:px-4"
                size="sm"
              >
                <Home className="h-4 w-4" />
                <span className="hidden md:inline">{language === 'fr' ? 'Retour au site' : 'Back to site'}</span>
              </Button>
              
              {/* Help button */}
              <Button
                variant="ghost"
                onClick={resetTutorial}
                className="flex items-center text-brand-600 hover:text-brand-700 hover:bg-brand-50 px-2"
                title={language === 'fr' ? 'Voir le tutoriel' : 'View tutorial'}
                size="sm"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
              
              {/* User info - hidden on mobile */}
              <div className="hidden md:block text-right" data-testid="user-info">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-600">{user.email}</p>
              </div>
              
              {/* Language switcher */}
              <LanguageSwitcher />
              
              {/* Partner Dashboard button - only for partners */}
              {user?.role?.includes('partner') && (
                <Button
                  onClick={() => navigate('/partner-dashboard')}
                  className="flex items-center gap-2 px-2 md:px-4 bg-brand-600 hover:bg-brand-700 text-white"
                  size="sm"
                  data-testid="partner-dashboard-btn"
                >
                  <Handshake className="h-4 w-4" />
                  <span className="hidden md:inline">Espace Partenaire</span>
                </Button>
              )}
              
              {/* Logout button - icon only on mobile */}
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-button"
                className="flex items-center gap-2 px-2 md:px-4"
                size="sm"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden md:inline">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Promo Code Banner */}
      {promoCodeInfo && promoCodeInfo.has_promo_code && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white" data-testid="promo-banner">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full">
                  <DollarSign className="h-5 w-5" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="font-semibold text-sm sm:text-base">
                    🎉 {promoCodeInfo.message || (language === 'fr' ? "Vous avez un code promo!" : "You have a promo code!")}
                  </p>
                  <p className="text-white/90 text-xs sm:text-sm">
                    {promoCodeInfo.code_type === 'multi_uses_limit' && promoCodeInfo.uses_remaining && (
                      <span>{promoCodeInfo.uses_remaining} utilisation{promoCodeInfo.uses_remaining > 1 ? 's' : ''} restante{promoCodeInfo.uses_remaining > 1 ? 's' : ''}</span>
                    )}
                    {promoCodeInfo.code_type === 'multi_uses_expiry' && promoCodeInfo.expires_at && (
                      <span>Valide jusqu'au {new Date(promoCodeInfo.expires_at).toLocaleDateString('fr-CA')}</span>
                    )}
                    {promoCodeInfo.code_type === 'single_use' && (
                      <span>{language === 'fr' ? 'Usage unique' : 'Single use'}</span>
                    )}
                    {promoCodeInfo.source === 'promotional' && promoCodeInfo.assigned_by && (
                      <span className="ml-2 opacity-75">• Offert par {promoCodeInfo.assigned_by.split('@')[0]}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2 flex items-center gap-2">
                  <code className="font-mono font-bold text-lg tracking-wider">{promoCodeInfo.promo_code}</code>
                  <button
                    onClick={copyPromoCode}
                    className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                    data-testid="copy-promo-btn"
                  >
                    {copiedPromoCode ? (
                      <span className="text-xs font-medium">✓</span>
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        {/* Simplified Header Section - Client Number + Profile Combined */}
        <Card className="mb-6 border-0 shadow-lg bg-gradient-to-br from-brand-600 via-indigo-600 to-brand-700 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Client Number - Left Side */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <User className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-sm text-white/80 font-medium mb-1">Votre numéro de client</p>
                  <div 
                    className="flex items-center gap-3 bg-white rounded-xl px-4 py-2 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all group"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(user.client_number);
                        toast.success("Numéro de client copié!");
                      } catch (err) {
                        toast.error("Impossible de copier");
                      }
                    }}
                    title="Cliquez pour copier"
                    data-testid="client-number-display"
                  >
                    <span className="font-mono text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-indigo-600 tracking-wider">
                      {user.client_number}
                    </span>
                    <Copy className="h-5 w-5 text-brand-400 group-hover:text-brand-600 transition-colors" />
                  </div>
                </div>
              </div>

              {/* Profile Info - Right Side */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                <div 
                  className="flex items-center gap-2 text-sm bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 cursor-pointer hover:bg-white/20 transition-colors group"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(profileData.email);
                      toast.success("Courriel copié!");
                    } catch (err) {
                      toast.error("Impossible de copier");
                    }
                  }}
                  title="Cliquez pour copier"
                >
                  <Mail className="h-4 w-4 text-white/70" />
                  <span className="text-white/90 truncate max-w-[200px]">{profileData.email}</span>
                  <Copy className="h-3.5 w-3.5 text-white/50 group-hover:text-white/90 transition-colors" />
                </div>
                {profileData.phone && (
                  <div 
                    className="flex items-center gap-2 text-sm bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 cursor-pointer hover:bg-white/20 transition-colors group"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(profileData.phone);
                        toast.success("Téléphone copié!");
                      } catch (err) {
                        toast.error("Impossible de copier");
                      }
                    }}
                    title="Cliquez pour copier"
                  >
                    <Phone className="h-4 w-4 text-white/70" />
                    <span className="text-white/90">{profileData.phone}</span>
                    <Copy className="h-3.5 w-3.5 text-white/50 group-hover:text-white/90 transition-colors" />
                  </div>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowProfileEdit(!showProfileEdit)}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                  data-testid="edit-profile-btn"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
              </div>
            </div>

            {/* Edit Profile Form - Collapsible */}
            {showProfileEdit && (
              <div className="mt-6 pt-6 border-t border-white/20">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-white/80">Prénom</Label>
                      <Input
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                        className="bg-white/90 border-0"
                        data-testid="profile-first-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-white/80">Nom</Label>
                      <Input
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                        className="bg-white/90 border-0"
                        data-testid="profile-last-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-white/80">Courriel</Label>
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        className="bg-white/90 border-0"
                        data-testid="profile-email"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-white/80">Téléphone</Label>
                      <Input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="bg-white/90 border-0"
                        data-testid="profile-phone"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4 gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowProfileEdit(false)}
                      className="text-white/80 hover:text-white hover:bg-white/10"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={saveProfile}
                      disabled={savingProfile}
                      size="sm"
                      className="bg-white text-brand-700 hover:bg-white/90"
                      data-testid="save-profile-btn"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {savingProfile ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stripe Connect Setup for Sellers - TEMPORAIREMENT MASQUÉ (Interac uniquement) */}

        {/* Partner Dashboard Card - Prominent for partner users */}
        {user?.role?.includes('partner') && (
          <Card 
            className="mb-6 border-2 border-brand-300 shadow-lg bg-gradient-to-r from-brand-50 via-white to-indigo-50 cursor-pointer hover:shadow-xl transition-all hover:border-brand-400 group"
            onClick={() => navigate('/partner-dashboard')}
            data-testid="partner-dashboard-nav-card"
          >
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-brand-600 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Handshake className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      Espace Partenaire
                      <span className="text-xs font-medium bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full">
                        {partnerPreview?.partner?.partner_type === 'admin_groupe' ? 'Admin Groupe' : 'Revendeur'}
                      </span>
                    </h3>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Gérez vos filleuls, commissions et votre code promo
                    </p>
                    {partnerPreview?.partner && (
                      <div className="flex flex-wrap items-center gap-4 mt-3">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Users className="h-4 w-4 text-blue-500" />
                          <span className="font-semibold text-gray-900">{partnerPreview.stats?.total_referrals || 0}</span>
                          <span className="text-gray-500">filleuls</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm">
                          <TrendingUp className="h-4 w-4 text-green-500" />
                          <span className="font-semibold text-gray-900">${(partnerPreview.stats?.commissions_this_month || 0).toFixed(2)}</span>
                          <span className="text-gray-500">ce mois</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm">
                          <Wallet className="h-4 w-4 text-amber-500" />
                          <span className="font-semibold text-gray-900">${(partnerPreview.stats?.commissions_pending || 0).toFixed(2)}</span>
                          <span className="text-gray-500">en attente</span>
                        </div>
                        <div className="bg-gray-100 rounded-lg px-3 py-1" onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(partnerPreview.partner.promo_code);
                          toast.success('Code promo copié!');
                        }}>
                          <span className="text-xs text-gray-500">Code: </span>
                          <code className="font-mono font-bold text-brand-600">{partnerPreview.partner.promo_code}</code>
                          <Copy className="h-3 w-3 inline ml-1 text-gray-400" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  className="bg-brand-600 hover:bg-brand-700 text-white gap-2 group-hover:gap-3 transition-all"
                  data-testid="partner-dashboard-cta-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/partner-dashboard');
                  }}
                >
                  Accéder
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stripe Connect Setup for Sellers - TEMPORAIREMENT MASQUÉ (Interac uniquement) */}
        {/* {(user.role === 'seller' || sellingCount > 0 || showStripeSetup) && (
          <div className="mb-6">
            <StripeConnectOnboarding user={user} />
          </div>
        )} */}

        {/* Payment Settings Card */}
        <Card className="mb-6 border-brand-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-base">
                <CreditCard className="h-5 w-5 mr-2 text-brand-600" />
                Informations de paiement
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPaymentSettings(!showPaymentSettings)}
                className="text-brand-600 border-brand-300"
              >
                <Settings className="h-4 w-4 mr-1" />
                {showPaymentSettings ? 'Fermer' : 'Configurer'}
              </Button>
            </div>
            {!showPaymentSettings && (
              <CardDescription className="text-sm mt-1">
                {paymentInfo.interac_email ? (
                  <span className="text-green-600">✓ Email Interac configuré: {paymentInfo.interac_email}</span>
                ) : (
                  <span className="text-amber-600">⚠️ Configurez votre email Interac pour recevoir vos paiements</span>
                )}
              </CardDescription>
            )}
          </CardHeader>
          
          {showPaymentSettings && (
            <CardContent className="space-y-4 pt-0">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p><strong>💡 Important:</strong> Ces informations sont utilisées pour les transferts de billets et les paiements.</p>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                    Email Interac (pour recevoir les paiements)
                  </label>
                  <Input
                    type="email"
                    placeholder="votre-email@banque.ca"
                    value={paymentInfo.interac_email}
                    onChange={(e) => setPaymentInfo({...paymentInfo, interac_email: e.target.value})}
                    className="border-gray-300"
                  />
                  <p className="text-xs text-gray-500">L'adresse où vous recevrez vos virements Interac</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    <Mail className="h-4 w-4 mr-1 text-purple-600" />
                    Courriel de la plateforme de billets
                  </label>
                  <Input
                    type="email"
                    placeholder="votre-email@exemple.com"
                    value={paymentInfo.ticketmaster_email}
                    onChange={(e) => setPaymentInfo({...paymentInfo, ticketmaster_email: e.target.value})}
                    className="border-gray-300"
                  />
                  <p className="text-xs text-gray-500">Votre courriel associé à la plateforme de billets sélectionnée (Ticketmaster, Evenko, etc.)</p>
                </div>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button
                  onClick={savePaymentInfo}
                  disabled={savingPaymentInfo}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {savingPaymentInfo ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Sauvegarder
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Create Transfer Request Button */}
        <div className="mb-6">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-brand-500 hover:bg-brand-600" data-testid="create-transfer-request-button">
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle demande de transfert
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 md:p-6">
              <DialogHeader>
                <DialogTitle className="text-lg md:text-xl">
                  {listingPrefillData
                    ? `Acheter — ${listingPrefillData.event_name}`
                    : "Nouvelle demande de transfert de billets"}
                </DialogTitle>
                <DialogDescription className="text-sm">
                  {listingPrefillData
                    ? "Formulaire pré-rempli depuis l'annonce. Vérifiez et confirmez les détails."
                    : "Créez une demande de transfert sécurisé en utilisant les numéros de client"}
                </DialogDescription>
              </DialogHeader>
              <TransferRequestForm
                key={listingPrefillData?.slug || "new"}
                user={user}
                prefillData={listingPrefillData}
                prefillSellerClientNumber={listingPrefillData?.seller_client_number || null}
                onSuccess={(...args) => {
                  setListingPrefillData(null);
                  handleTransactionCreated(...args);
                }}
                onCancel={() => {
                  setShowCreateDialog(false);
                  setListingPrefillData(null);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Transactions List with Tabs */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.myTransactions')}</CardTitle>
            <CardDescription>{t('dashboard.viewManage')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 mb-6">
                  <TabsTrigger value="all" data-testid="all-tab">
                    {t('dashboard.all')} ({transactions.length})
                  </TabsTrigger>
                  <TabsTrigger value="buying" data-testid="buying-tab">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {t('dashboard.buying')} ({buyingCount})
                  </TabsTrigger>
                  <TabsTrigger value="selling" data-testid="selling-tab">
                    <Store className="h-4 w-4 mr-2" />
                    {t('dashboard.selling')} ({sellingCount})
                  </TabsTrigger>
                  <TabsTrigger value="listings" data-testid="listings-tab">
                    <Ticket className="h-4 w-4 mr-2" />
                    Mes annonces
                  </TabsTrigger>
                </TabsList>

                {activeTab === "listings" ? (
                  <TabsContent value="listings">
                    <TicketListingsManager />
                  </TabsContent>
                ) : (
                <TabsContent value={activeTab}>
                  {filteredTransactions.length === 0 ? (
                    <div className="text-center py-8" data-testid="no-transactions">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">
                        {activeTab === "all" && t('dashboard.noTransactions')}
                        {activeTab === "buying" && t('dashboard.noBuyingTransactions')}
                        {activeTab === "selling" && t('dashboard.noSellingTransactions')}
                      </p>
                      <p className="text-sm text-gray-500">{t('dashboard.noTransactionsDesc')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="transactions-list">
                      {filteredTransactions.map((transaction) => {
                        const userRole = getUserRole(transaction);
                        const roleInfo = getRoleDisplay(userRole);
                        const RoleIcon = roleInfo.icon;
                        
                        return (
                          <div
                            key={transaction.id}
                            data-testid={`transaction-item-${transaction.id}`}
                            onClick={() => navigate(`/transactions/${transaction.id}`)}
                            className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:shadow-md cursor-pointer bg-white transition-shadow"
                          >
                            {/* Mobile: Stack layout */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {/* Event name and role badge */}
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base break-words">{transaction.event_name}</h3>
                                  <div className={`flex items-center space-x-1 text-xs ${roleInfo.color} flex-shrink-0`}>
                                    <RoleIcon className="h-3 w-3" />
                                    <span className="font-medium">{roleInfo.text}</span>
                                  </div>
                                </div>
                                
                                {/* Event details - responsive grid */}
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                                  <span className="flex items-center">
                                    <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                                    {formatDateFR(transaction.event_date)}
                                  </span>
                                  <span className="flex items-center font-medium">
                                    {formatPriceFR(transaction.price)}
                                  </span>
                                  <span>{transaction.ticket_count} {t('transaction.tickets')}</span>
                                </div>
                                
                                {/* Section/Row details */}
                                {(transaction.section || transaction.row || transaction.seats) && (
                                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                                    {transaction.section && <span className="truncate">{t('transaction.section')}: {transaction.section}</span>}
                                    {transaction.row && <span className="truncate">{t('transaction.row')}: {transaction.row}</span>}
                                    {transaction.seats && <span className="truncate">{t('transaction.seats')}: {transaction.seats}</span>}
                                  </div>
                                )}
                                
                                {/* Buyer/Seller info */}
                                {userRole === "buyer" && (
                                  <p className="text-xs text-gray-500 mt-1 truncate">{t('roles.seller')}: #{transaction.seller_client_number}</p>
                                )}
                                {userRole === "seller" && (
                                  <p className="text-xs text-gray-500 mt-1 truncate">{t('roles.buyer')}: #{transaction.buyer_client_number}</p>
                                )}
                              </div>
                              
                              {/* Status badge */}
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap self-start sm:self-center ${getStatusColor(transaction.status)}`}>
                                {formatStatus(transaction.status)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
                )}
              </Tabs>
            )}
          </CardContent>
        </Card>
        
        {/* Invoices Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-brand-600" />
              {language === 'fr' ? 'Factures et reçus' : 'Invoices and Receipts'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Documents disponibles pour vos transferts terminés' 
                : 'Documents available for your completed transfers'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InvoicesList token={localStorage.getItem('token')} />
          </CardContent>
        </Card>

        {/* User Preferences Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-brand-600" />
              {language === 'fr' ? 'Préférences de communication' : 'Communication Preferences'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Choisissez la langue de vos notifications' 
                : 'Choose the language for your notifications'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {language === 'fr' ? 'Langue des notifications (courriels et SMS)' : 'Notification language (emails and SMS)'}
                </label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="notification_language"
                      value="fr"
                      checked={(user?.preferred_language || 'fr') === 'fr'}
                      onChange={async (e) => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API}/auth/profile`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ preferred_language: 'fr' })
                          });
                          if (response.ok) {
                            toast.success(language === 'fr' ? 'Langue mise à jour' : 'Language updated');
                            if (refreshUserData) refreshUserData();
                          }
                        } catch (error) {
                          toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
                        }
                      }}
                      className="w-4 h-4 text-brand-600"
                    />
                    <span className="text-sm">Français</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="notification_language"
                      value="en"
                      checked={(user?.preferred_language || 'fr') === 'en'}
                      onChange={async (e) => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API}/auth/profile`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ preferred_language: 'en' })
                          });
                          if (response.ok) {
                            toast.success(language === 'fr' ? 'Langue mise à jour' : 'Language updated');
                            if (refreshUserData) refreshUserData();
                          }
                        } catch (error) {
                          toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
                        }
                      }}
                      className="w-4 h-4 text-brand-600"
                    />
                    <span className="text-sm">English</span>
                  </label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Onboarding Tutorial */}
      {showTutorial && (
        <OnboardingTutorial 
          onComplete={completeTutorial} 
          onSkip={skipTutorial} 
        />
      )}
      
      {/* Report bug link */}
      <div className="flex justify-center py-4 mt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={() => {
            const supportBtn = document.querySelector('[data-testid="floating-support-btn"]');
            if (supportBtn) supportBtn.click();
          }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-brand-500 transition-colors"
        >
          <Bug className="h-4 w-4" />
          <span>Rapporter un problème</span>
        </button>
      </div>
    </div>
  );
};

export default DashboardPage;